import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CreateUserKpiBody, UpdateUserKpiBody, UserKpiEntry } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { userKpiKeys, userKpisQueryOptions } from "@/lib/queries";
import { getUserFacingApiMessage } from "@/lib/api-client";
import {
  createUserKpi,
  deleteUserKpi,
  reorderUserKpis,
  shareUserKpiWithOrg,
  updateUserKpi,
  type UserKpiScope,
} from "../api/user-kpis.api";
import {
  applyUserKpiOrder,
  hiddenUserKpis,
  sortUserKpis,
  visibleUserKpis,
} from "../lib/user-kpi-model";

/**
 * Per-user KPI settings for one page domain, plus the mutations the drawer needs.
 *
 * Reorder and hide/color edits apply optimistically — they are presentation-only
 * and a round trip would make dragging feel laggy. Both roll back on failure.
 * Concurrent tabs are last-write-wins by design (see the plan's ordering note).
 */
export function useUserKpis(scope: UserKpiScope) {
  const tenantId = useAuth((s) => s.activeTenantId) ?? "";
  const queryClient = useQueryClient();
  const queryKey = userKpiKeys.scope(tenantId);

  const query = useQuery(userKpisQueryOptions(scope, tenantId));
  const entries = useMemo(() => sortUserKpis(query.data ?? []), [query.data]);
  const visible = useMemo(() => visibleUserKpis(entries), [entries]);
  const hidden = useMemo(() => hiddenUserKpis(entries), [entries]);
  const byName = useMemo(
    () => Object.fromEntries(entries.map((entry) => [entry.name, entry])) as Record<string, UserKpiEntry>,
    [entries],
  );

  // Tenant-scoped rather than page-scoped, so an edit made in either drawer is
  // immediately visible to every page-level picker.
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const onError = useCallback((error: unknown) => {
    toast.error(getUserFacingApiMessage(error));
  }, []);

  const createMutation = useMutation({
    mutationFn: (body: CreateUserKpiBody) => createUserKpi(scope, body),
    onSuccess: invalidate,
    onError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateUserKpiBody }) =>
      updateUserKpi(scope, id, body),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<UserKpiEntry[]>(queryKey);
      if (previous) {
        queryClient.setQueryData<UserKpiEntry[]>(
          queryKey,
          previous.map((entry) => (entry.id === id ? { ...entry, ...body } : entry)),
        );
      }
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      onError(error);
    },
    onSettled: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteUserKpi(scope, id),
    onSuccess: invalidate,
    onError,
  });

  const reorderMutation = useMutation({
    mutationFn: (names: string[]) => reorderUserKpis(scope, names),
    onMutate: async (names) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<UserKpiEntry[]>(queryKey);
      if (previous) {
        queryClient.setQueryData<UserKpiEntry[]>(queryKey, applyUserKpiOrder(previous, names));
      }
      return { previous };
    },
    onError: (error, _names, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      onError(error);
    },
    onSettled: invalidate,
  });

  const shareMutation = useMutation({
    mutationFn: (id: number) => shareUserKpiWithOrg(scope, id),
    onSuccess: invalidate,
    onError,
  });

  const setHidden = useCallback(
    (id: number, isHidden: boolean) => updateMutation.mutate({ id, body: { hidden: isHidden } }),
    [updateMutation],
  );

  return {
    ...query,
    tenantId,
    entries,
    visible,
    hidden,
    byName,
    createKpi: createMutation.mutate,
    createKpiAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateKpi: updateMutation.mutate,
    updateKpiAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteKpi: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    reorderKpis: reorderMutation.mutate,
    shareKpi: shareMutation.mutate,
    isSharing: shareMutation.isPending,
    setHidden,
  };
}
